import type { Article } from "../articles";

export const articleHurricaneSeasonHomePrep: Article = {
  slug: "2026-hurricane-season-home-prep",
  title: "Hurricane Season 2026: The Complete Home Preparation Checklist",
  description: "Hurricane season runs June 1-November 30. Start preparing now: secure your roof, trim trees, stock supplies, and know your evacuation zone.",
  category: "Seasonal",
  tags: ["Hurricane", "Storm Prep", "Home Safety", "Emergency", "Seasonal Maintenance"],
  createdAt: "June 20, 2026",
  updatedAt: "July 1, 2026",
  lastVerified: "July 1, 2026",
  readTime: "10 min read",
  keyTakeaway: `Start hurricane prep 2-3 weeks before season begins. Key tasks: inspect roof and flashing, trim trees, clean gutters, stock 7-day supplies (water, food, meds), know your evacuation zone, and review insurance coverage. Don't wait for a named storm.`,
  author: { name: "Mauricio L.", url: "/authors/mauricio-l/" },
  sources: [
    {
      name: "Federal Emergency Management Agency (FEMA) - Hurricane Preparedness",
      url: "https://www.ready.gov/hurricanes"
    },
    {
      name: "National Hurricane Center (NHC)",
      url: "https://www.nhc.noaa.gov"
    },
    {
      name: "Insurance Information Institute - Hurricane Coverage",
      url: "https://www.iii.org"
    }
  ],
  keyMetrics: [
    { label: "Hurricane Season", value: "June 1 - November 30" },
    { label: "Water Supply", value: "1 gallon/person/day × 7 days" },
    { label: "Evacuation Decision", value: "Know your zone (A, B, C)" },
    { label: "Peak Season", value: "August 15 - October 15" }
  ],
  faqs: [
    {
      question: "When should I start preparing for hurricane season?",
      answer: "Start 2-3 weeks before June 1st. This gives you time to complete home maintenance, stock supplies, and review insurance without the panic-buying crowds. Peak season is August 15 - October 15, but early storms (like Allison or Andrea) can form in June."
    },
    {
      question: "How much water and food should I stockpile?",
      answer: "FEMA recommends 1 gallon of water per person per day for 7 days (drinking + sanitation). For a family of 4, that's 28 gallons. Store non-perishable food for 7 days: canned goods, protein bars, peanut butter, dried fruit. Don't forget pet food and medications."
    },
    {
      question: "Should I board up windows or install hurricane shutters?",
      answer: "Hurricane shutters (accordion, roll-down, or Bahama) are permanent solutions that add home value. Plywood works in a pinch but requires pre-cutting and storage. Impact-resistant windows are the premium option ($800-$1,500 per window) but eliminate the need for shutters entirely."
    }
  ],
  content: `
    <p>
      Every year, the same scene plays out across coastal communities: A tropical wave forms off the coast of Africa. It strengthens into a tropical depression, then a named storm. Forecast models show it heading toward the Gulf or Southeast coast. And suddenly, thousands of people are standing in line at Home Depot at 7 PM, fighting over the last sheets of plywood and gallon jugs of water.
    </p>
    <p>
      Hurricane preparedness isn't a three-day sprint. It's a season-long marathon that starts weeks before June 1st and ends after November 30th. The homeowners who weather storms successfully aren't the ones who panicked and boarded up: they're the ones who inspected their roof in May, trimmed trees in April, stocked supplies gradually, and knew their evacuation zone before a storm had a name.
    </p>
    <p>
      This is your 2026 hurricane preparation checklist. Start now, before the first named storm forms.
    </p>

    <h2>Phase 1: Home Hardening (4-6 Weeks Before Season)</h2>
    <p>
      These tasks take time and may require contractors. Do them early:
    </p>

    <h3>Roof Inspection and Repairs</h3>
    <p>
      Your roof is your first line of defense. Have a licensed roofer inspect for:
    </p>
    <ul>
      <li><strong>Loose or damaged shingles:</strong> Wind can lift compromised shingles, creating openings for water.</li>
      <li><strong>Flashing integrity:</strong> Check around chimneys, vents, skylights, and roof valleys. Flashing failures cause 70% of roof leaks.</li>
      <li><strong>Roof deck condition:</strong> Soft spots or sagging indicate structural issues that worsen under wind load.</li>
      <li><strong>Age:</strong> If your roof is 15+ years old, check for <a href="/articles/10-signs-you-need-a-new-roof/" class="text-[var(--accent)] hover:underline font-semibold">signs you need a new roof</a> and learn <a href="/articles/how-to-photograph-roof-for-insurance-claim/" class="text-[var(--accent)] hover:underline font-semibold">how to photograph your roof for an insurance claim</a> before hurricane season. You can also estimate replacement budget with our <a href="/calculators/roofing/" class="text-[var(--accent)] hover:underline font-semibold">roof replacement cost calculator</a>.</li>
    </ul>

    <!--COMPONENT:pullquote-->Keeping wind from entering your home is the difference between a minor cleanup and losing your roof entirely. Structural envelope integrity is everything.<!--ENDOFCOMPONENT-->

    <h3>Tree Trimming and Yard Work</h3>
    <p>
      Overhanging branches become projectiles in 60+ mph winds:
    </p>
    <ul>
      <li><strong>Trim dead or weak branches:</strong> Any branch within 10 feet of your roof should be removed.</li>
      <li><strong>Remove dead trees:</strong> If a tree is more than 50% dead, remove it entirely. It won't survive a tropical storm.</li>
      <li><strong>Secure outdoor furniture:</strong> Store or anchor grills, patio furniture, potted plants, and decorations. These become missiles in high winds.</li>
      <li><strong>Clean gutters and drains:</strong> Clogged gutters overflow, causing water damage. Clear debris from yard drains to prevent flooding.</li>
    </ul>

    <h3>Window and Door Protection</h3>
    <p>
      Breached windows equal catastrophic interior damage:
    </p>
    <ul>
      <li><strong>Install hurricane shutters:</strong> Accordion, roll-down, or colonial shutters provide permanent protection. Install now, before storm panic.</li>
      <li><strong>Pre-cut plywood:</strong> If using plywood, measure and cut panels now. Label each panel by window. Store with screws and washers.</li>
      <li><strong>Reinforce garage doors:</strong> Garage door failure causes structural pressurization. Install a retrofit bracing kit or replace with a wind-rated door (see our <a href="/articles/wind-rated-garage-doors-guide/" class="text-[var(--accent)] hover:underline font-semibold">wind-rated garage doors guide</a>).</li>
      <li><strong>Check impact-resistant windows:</strong> If you have them, verify certification labels and inspect seals for damage. Otherwise, check out the <a href="/articles/impact-windows-vs-hurricane-shutters-cost/" class="text-[var(--accent)] hover:underline font-semibold">impact windows vs hurricane shutters cost comparison</a>.</li>
    </ul>

    <!--COMPONENT:comparison-table|json={"columns":[{"key":"item","label":"Prep Component"},{"key":"purpose","label":"Storm Purpose"},{"key":"quantity","label":"Typical Quantity"},{"key":"action","label":"Pre-Season Action"}],"rows":[{"item":"5/8-inch Plywood Sheets","purpose":"Window impact barrier","quantity":"Fits all windows","action":"Cut, label, and drill pilot holes"},{"item":"Tapcon / Wood Anchors","purpose":"Secure plywood to framing","quantity":"8 - 12 per window","action":"Verify correct anchor sizes"},{"item":"Hurricane Bracing Kit","purpose":"Reinforce garage door","quantity":"1 - 2 steel braces","action":"Dry-fit brackets on floor/ceiling"},{"item":"Exterior Caulk / Tape","purpose":"Seal minor entry points","quantity":"2 - 4 tubes / rolls","action":"Inspect window flashing seals"}]}--><!--ENDOFCOMPONENT-->

    <div class="my-6 p-4 rounded-lg bg-amber-500/10 border border-amber-500/30">
      <p class="font-bold text-foreground m-0 mb-2">Critical Reminder</p>
      <p class="text-sm text-text-muted m-0">
        Don't wait for a named storm to start preparing. Contractors book up, supplies sell out, and prices surge when a storm approaches. Start your hurricane prep 4-6 weeks before June 1st. Your future self will thank you.
      </p>
    </div>

    <h2>Phase 2: Supply Stockpiling (2-3 Weeks Before Season)</h2>
    <p>
      Build your emergency kit gradually. Buy a little extra each shopping trip:
    </p>

    <h3>Water and Food</h3>
    <ul>
      <li><strong>Water:</strong> 1 gallon per person per day for 7 days. For a family of 4: 28 gallons minimum. Store in food-grade containers. Rotate every 6 months.</li>
      <li><strong>Non-perishable food:</strong> Canned meats, vegetables, fruits, beans. Protein bars, peanut butter, crackers, dried fruit. Plan for 7 days of meals.</li>
      <li><strong>Manual can opener:</strong> Electric openers don't work without power.</li>
      <li><strong>Pet food:</strong> 7-day supply in waterproof containers.</li>
    </ul>

    <h3>Medical and Health</h3>
    <ul>
      <li><strong>Prescription medications:</strong> 14-day supply (7-day storm supply + 7-day post-storm buffer). Many pharmacies offer emergency refill waivers during hurricanes.</li>
      <li><strong>First aid kit:</strong> Bandages, antiseptic, pain relievers, anti-diarrheal, burn cream.</li>
      <li><strong>Medical equipment:</strong> Backup batteries for CPAP machines, oxygen concentrators, or mobility devices.</li>
      <li><strong>Sanitation:</strong> Moist towelettes, garbage bags, toilet paper, feminine hygiene products.</li>
    </ul>

    <h3>Power and Communication</h3>
    <ul>
      <li><strong>Batteries:</strong> AA, AAA, C, D, and 9V for flashlights, radios, and devices.</li>
      <li><strong>Flashlights:</strong> One per family member. Avoid candles (fire hazard).</li>
      <li><strong>NOAA weather radio:</strong> Battery-powered or hand-crank. Receives emergency alerts even when cell towers are down.</li>
      <li><strong>Phone chargers:</strong> Portable battery packs (20,000+ mAh). Car chargers. Solar chargers for extended outages.</li>
      <li><strong>Generator:</strong> If you have one, test it now. Store fuel safely (5-day supply minimum). Never run generators indoors or in garages.</li>
    </ul>

    <!--COMPONENT:info-note|label="Critical Generator Safety"-->Never, under any circumstances, run a portable generator inside a home, garage, basement, or crawlspace. Carbon monoxide is an odorless, colorless killer that can build up to fatal levels in minutes. Keep your generator at least 20 feet away from all windows, doors, and vents, with the exhaust pointing away from your house. Always let the generator cool down before refueling to prevent gasoline from igniting on hot engine parts.<!--ENDOFCOMPONENT-->

    <h3>Documents and Cash</h3>
    <ul>
      <li><strong>Important documents:</strong> Insurance policies, IDs, medical records, bank account info. Store in waterproof bags or scan to cloud storage.</li>
      <li><strong>Cash:</strong> ATMs and credit card machines don't work without power. Keep $200-500 in small bills ($1s, $5s, $10s, $20s).</li>
      <li><strong>Emergency contacts:</strong> Written list of family, doctors, insurance agents, and contractors. Don't rely on your phone's contact list.</li>
    </ul>

    <h2>Phase 3: Know Your Evacuation Zone (Before Season Starts)</h2>
    <p>
      Evacuation orders are based on zones, not emotions:
    </p>
    <ul>
      <li><strong>Find your zone:</strong> Check your county's emergency management website. Zones are typically labeled A (coastal, evacuate first), B (inland from A), C (inland from B), etc.</li>
      <li><strong>Plan your route:</strong> Identify 2-3 evacuation routes. Traffic jams are inevitable. Have alternatives.</li>
      <li><strong>Identify lodging:</strong> Book hotels inland or with family/friends outside evacuation zones. Popular destinations fill up fast.</li>
      <li><strong>Know when to leave:</strong> If you're in a mobile home or Zone A, evacuate for Category 1+. If you're in Zone B, evacuate for Category 2+. Don't wait for mandatory orders, by then, it's too late.</li>
    </ul>

    <h2>Phase 4: Insurance Review (3-4 Weeks Before Season)</h2>
    <p>
      Storm season reveals coverage gaps:
    </p>
    <ul>
      <li><strong>Review your policy:</strong> Does it cover wind damage? Flood damage? (Spoiler: Standard homeowners insurance covers wind but NOT flood. Flood requires separate NFIP or private flood insurance.)</li>
      <li><strong>Document your home:</strong> Walk through every room with your phone. Video every closet, cabinet, and storage area. Upload to cloud storage. This is your proof if you file a claim.</li>
      <li><strong>Check deductibles:</strong> Hurricane deductibles are often 2-5% of dwelling coverage, not flat $500-$1,000. On a $300,000 home, a 2% deductible is $6,000 out-of-pocket.</li>
      <li><strong>Update coverage:</strong> If you've renovated or added valuable items, increase coverage limits. Replacement cost should reflect current construction costs.</li>
    </ul>

    <h2>Phase 5: Final Prep (When a Storm Is 5-7 Days Out)</h2>
    <p>
      Once a storm forms and forecasts show it heading your way:
    </p>
    <ul>
      <li><strong>Fill prescriptions:</strong> Pharmacies close or have long waits.</li>
      <li><strong>Fill gas tanks:</strong> Cars and generators. Gas stations lose power and can't pump fuel.</li>
      <li><strong>Withdraw cash:</strong> ATMs go down during outages.</li>
      <li><strong>Freeze water:</strong> Fill plastic bottles with water and freeze. They keep food cold if power goes out, then provide drinking water as they melt.</li>
      <li><strong>Clean and prep:</strong> Wash dishes, do laundry, clean the house. Life is harder without running water and electricity.</li>
      <li><strong>Charge everything:</strong> Phones, tablets, battery packs, power tools.</li>
    </ul>

    <h2>Summary: Preparation Gives You Control</h2>
    <p>
      Hurricane preparedness isn't about fear: it's about control. You can't control where storms form or where they track. But you can control whether your roof holds, whether your family has water and food, and whether you know when to evacuate.
    </p>
    <p>
      Start now. Not when the National Hurricane Center issues a watch. Not when your county announces evacuation orders. Now. The time you invest in May and June pays dividends when the storm surge rises and the wind howls.
    </p>
  `
};